import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { OneEmptyState } from '@factorialco/factorial-one-react/dist/experimental'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import useCreateUpsellRequest from 'modules/upsell/hooks/useCreateUpsellRequest'
import { useGetUpsellingData } from 'modules/upselling_kit/hooks/useGetUpsellingData'
import { UpsellingKitEmptyState } from 'modules/upselling_kit/types'
import { useDefaultMessages } from 'modules/upselling_kit/utils/defaultMessages'
import {
  handleUpsellingLearnMore,
  handleUpsellingVisibility,
} from 'modules/upselling_kit/utils/handlers'

import { UpsellGuard } from '../UpsellGuard'

const EmptyStateContent = ({
  moduleRequested,
  title,
  description,
  requestSource,
  requestReason,
  featureRequested,
  background = '',
  onClick,
  learnMoreNavigate,
}: UpsellingKitEmptyState) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { create } = useCreateUpsellRequest()
  const { moduleSlug } = useGetUpsellingData({
    moduleRequested,
  })

  const handleUpsell = async () => {
    await create({
      moduleRequested: moduleRequested,
      requestSource: requestSource,
      requestReason: requestReason,
      featureRequested: featureRequested,
    })
  }

  const handleOnClick = () => {
    onClick?.()
    if (learnMoreNavigate) {
      navigate(learnMoreNavigate)
    } else {
      navigate(navigation.discover.marketplaceModules.item.fullPath({ id: moduleSlug || '' }))
    }
    handleUpsellingLearnMore({
      moduleRequested,
      requestSource,
      component: 'empty_state',
    })
  }

  useEffect(() => {
    handleUpsellingVisibility({
      moduleRequested,
      requestSource,
      component: 'empty_state',
    })
  }, [moduleRequested, requestSource])

  const defaultMessages = useDefaultMessages()

  return (
    <Box
      width="full"
      justifyContent="center"
      alignItems="center"
      position="relative"
      style={{
        height: '100cqh',
        overflow: 'hidden',
      }}
    >
      <Box
        position="absolute"
        width="full"
        height="full"
        style={{
          backgroundImage: `url(${background})`,
          backgroundPosition: 'top center',
          filter: 'blur(4px)',
        }}
      />
      <OneEmptyState
        title={title || ''}
        description={description || ''}
        emoji={'⚡️'}
        actions={[
          {
            label: i18n.t('upselling_kit.button.learn_more'),
            onClick: handleOnClick,
            type: 'default',
            variant: 'outline',
          },
          {
            label: i18n.t('upselling_kit.button.request_information'),
            onClick: handleUpsell,
            type: 'upsell',
            errorMessage: defaultMessages.error,
            successMessage: defaultMessages.success,
            loadingState: defaultMessages.loading,
            nextSteps: defaultMessages.nextSteps,
            closeLabel: defaultMessages.close,
          },
        ]}
      />
    </Box>
  )
}

const EmptyState = ({
  title,
  description,
  fallbackComponent,
  ...upsellProps
}: UpsellingKitEmptyState) => {
  return (
    <UpsellGuard {...upsellProps} fallbackComponent={fallbackComponent || null}>
      <EmptyStateContent {...upsellProps} title={title} description={description} />
    </UpsellGuard>
  )
}
export { EmptyState }
